import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './AddEditPortfolio.module.css'; // NEW STYLES IMPORT
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import showToast from '../../components/ShowToast';
import Loader2 from '../../components/loader2';
import PostcodeAutocomplete from '../../components/PostcodeAutocomplete/PostcodeAutocomplete';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS"; // Added Navbar
import { ArrowLeft, Camera, FileText, CheckCircle, X, Home } from 'lucide-react'; // Added Home icon

const AddEditBusinessPortfolio = () => {
    const navigate = useNavigate();
    const { portfolioId } = useParams();
    const isEditing = !!portfolioId;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [postcode, setPostcode] = useState('');
    const [suburb, setSuburb] = useState('');
    const [uploadedImages, setUploadedImages] = useState([]); 
    const [filesToRemove, setFilesToRemove] = useState([]);
    
    const [username, setUsername] = useState(''); // For Navbar
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 1. Get Username for Navbar
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUsername(decoded.UserName || 'User');
            } catch (e) {
                console.error("Token error", e);
            }
        }
    }, []);

    useEffect(() => {
        if (isEditing) {
            const fetchPortfolio = async () => {
                setIsLoading(true);
                try {
                    const businessId = localStorage.getItem('loggedInBusinessId');
                    const response = await axios.get(`${API_BASE_URL}/api/WorkPortfolio/GetWorkPortfolioDetail?portfolioId=${portfolioId}&businessId=${businessId}`);
                    const data = response.data.model;
                    setTitle(data.title);
                    setDescription(data.description);
                    setPostcode(data.postcode);
                    setSuburb(data.suburb);
                    setUploadedImages(data.mediaUrl.map(m => ({ key: m.id, url: m.url, file: null, isExisting: true, id: m.id })));
                } catch (err) {
                    setError('Failed to fetch portfolio details.');
                    showToast('error', 'Failed to fetch portfolio details.');
                    console.error(err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchPortfolio();
        }
    }, [portfolioId, isEditing]);

    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const newImages = files.map(file => ({
            key: `new-${file.name}-${Date.now()}`,
            url: URL.createObjectURL(file),
            file: file,
            isExisting: false,
            id: null
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
    };

    const handlePostcodeSelect = (locationObject) => {
        setPostcode(locationObject.fullAddress); 
        setSuburb(locationObject.suburb);       
    };

    const removeImage = (key, id) => {
        if (isEditing && id) {
            setFilesToRemove(prev => [...prev, id]);
        }
        setUploadedImages(prev => prev.filter(img => img.key !== key));
    };

    const handleSubmit = async () => {
        if (!title || !description || !postcode || !suburb) {
            showToast('error', 'Please fill all fields.');
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        const businessId = localStorage.getItem('loggedInBusinessId');

        formData.append('businessId', businessId);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('postcode', postcode);
        formData.append('suburb', suburb);
        formData.append('baseUrl', window.location.origin);

        if (isEditing) {
            formData.append('portfolioId', portfolioId);
        }

        uploadedImages.forEach(img => {
            if (img.file) {
                formData.append('Files', img.file);
            }
        });

        if (filesToRemove.length > 0) {
            filesToRemove.forEach(id => formData.append('filesToRemove', id));
        }

        const endpoint = isEditing ? `${API_BASE_URL}/api/WorkPortfolio/UpdateWorkPortfolio` : `${API_BASE_URL}/api/WorkPortfolio/CreateWorkPortfolio`;
        const method = isEditing ? 'put' : 'post';

        try {
            await axios[method](endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showToast('success', `Portfolio ${isEditing ? 'updated' : 'created'} successfully!`);
            navigate('/business-portfolios');
        } catch (err) {
            setError(`Failed to ${isEditing ? 'update' : 'create'} portfolio.`);
            showToast('error', `Failed to ${isEditing ? 'update' : 'create'} portfolio.`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            {isLoading && <Loader2 />}
            
            {/* 1. Add Navbar */}
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'portfolios'} />

            {/* 2. Fix Title Bar Alignment */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <button onClick={() => navigate(-1)} className={styles.btnGhost}>
                            <ArrowLeft className={styles.iconMd} />
                        </button>
                        
                        <div className={styles.headerLogo}>
                            <Home className={styles.iconMd} color="#022d6b" /> 
                            <span>Build Start</span>
                        </div>
                        
                        <div className={styles.separator}></div>
                        
                        <h1 className={styles.headerTitle}>{isEditing ? 'Edit Portfolio' : 'Create Portfolio'}</h1>
                    </div>
                </div>
            </header>

            <div className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <FileText size={24} color="#2563eb" />
                        <h3 className={styles.cardTitle}>Portfolio Information</h3>
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.gridCols2}>
                            <div>
                                <label htmlFor="title" className={styles.label}>Title</label>
                                <input id="title" type="text" placeholder="e.g., Modern Kitchen Remodel" value={title} onChange={(e) => setTitle(e.target.value)} className={styles.input} />
                            </div>
                            <div>
                                <label htmlFor="postcode" className={styles.label}>Postcode</label>
                                <PostcodeAutocomplete 
                                    value={postcode} 
                                    onSuggestionSelect={handlePostcodeSelect} 
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="suburb" className={styles.label}>Suburb</label>
                            <input id="suburb" type="text" placeholder="e.g., Sydney" value={suburb} onChange={(e) => setSuburb(e.target.value)} className={styles.input} readOnly />
                        </div>
                        <div>
                            <label htmlFor="description" className={styles.label}>Description</label>
                            <textarea id="description" placeholder="Describe the project..." value={description} onChange={(e) => setDescription(e.target.value)} className={styles.textarea}></textarea>
                        </div>
                    </div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <Camera size={24} color="#d97706" />
                        <h3 className={styles.cardTitle}>Project Images</h3>
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.uploadArea}>
                            <label htmlFor="file-upload" className={styles.btnOutline}>
                                <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                    <Camera size={18}/> Browse Files
                                </div>
                                <input id="file-upload" type="file" multiple accept="image/*" className={styles.hidden} onChange={handleImageUpload} />
                            </label>
                        </div>
                        {uploadedImages.length > 0 && (
                            <div className={styles.imagesGrid}>
                                {uploadedImages.map((image) => (
                                    <div key={image.key} className={styles.imageItem}>
                                        <img src={image.url} alt="upload preview" className={styles.uploadedImage} />
                                        <button type="button" className={styles.removeBtn} onClick={() => removeImage(image.key, image.id)}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{marginTop: '1.5rem'}}>
                    <button type="button" onClick={handleSubmit} className={styles.btnPrimary}>
                        <CheckCircle size={20} />
                        {isEditing ? 'Save Changes' : 'Create Portfolio'}
                    </button>
                </div>
                {error && <p style={{color:'#ef4444', marginTop:'1rem'}}>{error}</p>}
            </div>
        </div>
    );
};

export default AddEditBusinessPortfolio;